<script lang="ts">
  import QRCode from 'qrcode';
  import Dialog from './Dialog.svelte';
  import Icon from './Icon.svelte';

  interface Permissions {
    /** Current state — true = anyone with the link can edit. */
    linkEditEnabled: boolean;
    /** Caller persists the new state. Throw on failure to keep the
     *  control disabled / show an error; resolve to commit. */
    onChange: (next: boolean) => Promise<void>;
  }

  let {
    token,
    onClose,
    permissions,
  }: {
    token: string;
    onClose: () => void;
    /** Pass to surface a "View only / Edit" toggle. Only callers
     *  that know the viewer is the trip owner should provide this. */
    permissions?: Permissions;
  } = $props();

  const url = $derived(`${window.location.origin}/t/${token}`);
  let qrSvg: string = $state('');
  let copied = $state(false);
  let copiedTimer: ReturnType<typeof setTimeout> | null = null;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  $effect(() => {
    let cancelled = false;
    const target = url;
    QRCode.toString(target, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' })
      .then((svg) => {
        if (!cancelled) qrSvg = svg;
      })
      .catch((e) => console.error('QR generation failed:', e));
    return () => {
      cancelled = true;
    };
  });

  $effect(() => () => {
    if (copiedTimer) clearTimeout(copiedTimer);
  });

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
      if (copiedTimer) clearTimeout(copiedTimer);
      copiedTimer = setTimeout(() => (copied = false), 1600);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  }

  async function onNativeShare() {
    try {
      await navigator.share({ url });
    } catch (e) {
      // User cancellation throws AbortError; ignore.
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error('Share failed:', e);
    }
  }

  let savingPermissions: boolean = $state(false);
  let permissionsError: string | null = $state(null);

  async function setLinkEditEnabled(next: boolean) {
    if (!permissions || savingPermissions || permissions.linkEditEnabled === next) return;
    savingPermissions = true;
    permissionsError = null;
    try {
      await permissions.onChange(next);
    } catch (e) {
      permissionsError = e instanceof Error ? e.message : 'Could not update permissions';
    } finally {
      savingPermissions = false;
    }
  }
</script>

<Dialog {onClose} variant="sheet">
  <div class="panel" role="dialog" aria-modal="true" aria-label="Share this trip">
    <header class="head">
      <h2>Share this trip</h2>
      <button class="close-btn" type="button" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={18} />
      </button>
    </header>

    <p class="hint">Scan to open on another device, or copy the link to share.</p>

    <div class="qr-wrap" aria-hidden="true">
      {#if qrSvg}
        <!-- qrcode library output: a static SVG, no user input — safe to interpolate. -->
        {@html qrSvg}
      {:else}
        <div class="qr-skeleton"></div>
      {/if}
    </div>

    {#if permissions}
      <div class="permissions" role="group" aria-label="Link permissions">
        <span class="permissions-label">Anyone with this link can</span>
        <div class="view-toggle" role="radiogroup">
          <button
            type="button"
            role="radio"
            class="view-toggle__seg {!permissions.linkEditEnabled ? 'view-toggle__seg--active' : ''}"
            aria-checked={!permissions.linkEditEnabled}
            disabled={savingPermissions}
            onclick={() => setLinkEditEnabled(false)}
          >View only</button>
          <button
            type="button"
            role="radio"
            class="view-toggle__seg {permissions.linkEditEnabled ? 'view-toggle__seg--active' : ''}"
            aria-checked={permissions.linkEditEnabled}
            disabled={savingPermissions}
            onclick={() => setLinkEditEnabled(true)}
          >Edit</button>
        </div>
        {#if permissionsError}
          <p class="permissions-error">{permissionsError}</p>
        {/if}
      </div>
    {/if}

    <div class="url" role="region" aria-label="Trip URL">{url}</div>

    <div class="actions">
      <button class="btn btn-secondary copy-btn" type="button" onclick={onCopy}>
        <span>{copied ? 'Copied' : 'Copy link'}</span>
      </button>
      {#if canShare}
        <button class="btn btn-primary share-btn" type="button" onclick={onNativeShare}>
          <Icon name="share" size={15} strokeWidth={2} />
          <span>Share</span>
        </button>
      {/if}
    </div>
  </div>
</Dialog>

<style>
  .panel {
    position: relative;
    background: var(--color-canvas);
    width: 100%;
    max-width: 480px;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    box-shadow: rgba(15, 15, 15, 0.2) 0 -8px 32px -4px;
    padding: 1.5rem;
    padding-bottom: 1.75rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
  @media (min-width: 768px) {
    .panel {
      border-radius: var(--radius-md);
      box-shadow:
        rgba(0, 0, 0, 0.02) 0 0 0 1px,
        rgba(0, 0, 0, 0.04) 0 2px 6px 0,
        rgba(0, 0, 0, 0.10) 0 4px 8px 0;
    }
  }

  .head {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .head h2 { margin: 0; font-size: 18px; font-weight: 600; color: var(--color-ink); }
  .close-btn {
    background: transparent;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-muted);
  }
  .close-btn:hover { background: var(--color-surface-soft); color: var(--color-ink); }

  .hint {
    margin: 0;
    align-self: stretch;
    font-size: 13px;
    color: var(--color-muted);
    line-height: 1.4;
  }

  .qr-wrap {
    width: 180px;
    height: 180px;
    background: var(--color-canvas);
    padding: 0;
    border-radius: var(--radius-sm);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  @media (min-width: 768px) {
    .qr-wrap { width: 220px; height: 220px; }
  }
  .qr-wrap :global(svg) { width: 100%; height: 100%; display: block; }
  .qr-skeleton {
    width: 100%;
    height: 100%;
    background: var(--color-surface-soft);
    border-radius: var(--radius-sm);
  }

  .url {
    align-self: stretch;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    color: var(--color-ink);
    background: var(--color-surface-soft);
    padding: 0.625rem 0.875rem;
    border-radius: var(--radius-sm);
    word-break: break-all;
    text-align: center;
  }

  .actions {
    align-self: stretch;
    display: flex;
    gap: 0.5rem;
  }
  .copy-btn,
  .share-btn {
    flex: 1;
    justify-content: center;
    min-height: 44px;
    height: 44px;
    padding: 0 1rem;
    font-size: 14px;
  }

  .permissions {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
  .permissions-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .permissions-error {
    margin: 0;
    font-size: 12px;
    color: var(--color-error);
  }
</style>
